import { __awaiter } from "tslib";
import { LoadedState, ReadyState, isNotReadyOrFailedToLoad, timeoutByConnection } from "../shared/index.js";
const state = {
    loadedState: LoadedState.NOT_LOADED,
    readyState: ReadyState.NOT_READY
};
let onReadyResolver;
let settingsServiceFactory;
const onReadyPromise = new Promise((resolve) => {
    onReadyResolver = resolve;
});
const waitForReadyOrTimeout = () => Promise.race([timeoutByConnection(), onReadyPromise]);
const onReady = () => {
    var _a;
    const win = window;
    state.readyState = ReadyState.READY;
    settingsServiceFactory = (_a = win.pulse) === null || _a === void 0 ? void 0 : _a.settingsFactory;
    onReadyResolver();
};
const onLoadedStateChanged = (loadedState) => {
    state.loadedState = loadedState;
};
const ensureLoadedAndGetData = (defaultValue, getData, defaults, retryLoading) => {
    if (state.loadedState === LoadedState.FAILED_TO_LOAD_IN_BROWSER) {
        retryLoading === null || retryLoading === void 0 ? void 0 : retryLoading();
        return Promise.resolve(defaultValue);
    }
    if (state.readyState === ReadyState.NOT_READY) {
        return waitForReadyOrTimeout().then(() => {
            if (state.readyState === ReadyState.NOT_READY) {
                return Promise.resolve(defaultValue);
            }
            return getData(defaults);
        });
    }
    return getData(defaults);
};
const getDefferedSettings = (tag, name, defaults, remoteSettingEnabled, attributes = {}, updateSettingChange = false) => {
    return () => {
        return settingsServiceFactory
            .createPulseSettings(defaults, remoteSettingEnabled, attributes)
            .get(tag, name, updateSettingChange);
    };
};
const getDefferedSegments = (defaults, attributes) => {
    return () => {
        return settingsServiceFactory
            .createPulseSettings(defaults, true, attributes)
            .getSegmentsIds();
    };
};
const getDefferedExperiments = (defaults, attributes) => {
    return () => {
        return settingsServiceFactory
            .createPulseSettings(defaults, true, attributes)
            .getExperiments();
    };
};
/*
    deferred will only be called if the sdk loading is in progress, or failed to load
    in such case, it will start listening on the onReadyPromise immediately, to assign the deferred service ASAP
    instead of actually doing it during the get call.
*/
const deferred = (defaults, remoteSettingEnabled, attributes, retryLoading) => {
    return {
        get: ({ tag, name, updateSettingChange }) => __awaiter(void 0, void 0, void 0, function* () {
            return ensureLoadedAndGetData(defaults[name], getDefferedSettings(tag, name, defaults, remoteSettingEnabled, attributes, updateSettingChange), defaults, retryLoading);
        }),
        getSegments: () => __awaiter(void 0, void 0, void 0, function* () {
            return ensureLoadedAndGetData([], getDefferedSegments(defaults, attributes), defaults);
        }),
        getExperiments: () => __awaiter(void 0, void 0, void 0, function* () {
            return ensureLoadedAndGetData([], getDefferedExperiments(defaults, attributes), defaults);
        })
    };
};
const initRemoteSettings = (config, retryLoading) => {
    const remoteSettingEnabled = config.remoteSettingEnabled === undefined ? true : config.remoteSettingEnabled;
    if (isNotReadyOrFailedToLoad(state.readyState, state.loadedState) || !(pulse === null || pulse === void 0 ? void 0 : pulse._initialized)) {
        return deferred(config.defaults || {}, remoteSettingEnabled, config.attributes || {}, retryLoading);
    }
    const settingsService = pulse.settingsFactory.createPulseSettings(config.defaults, remoteSettingEnabled, config.attributes || {});
    return {
        get: ({ tag, name, updateSettingChange = false }) => {
            return settingsService.get(tag, name, updateSettingChange);
        },
        getSegments: () => settingsService.getSegmentsIds(),
        getExperiments: () => settingsService.getExperiments()
    };
};
export { initRemoteSettings, onLoadedStateChanged as onSettingsLoadingStateChanged, onReady as onSettingsReady, state as settingsState };
//# sourceMappingURL=settings.service.js.map